import { useMemo } from 'react';
import { ShortcutCommandEnum, ShortcutContextEnum } from '@mark43/cad-api';
import { Box, IconButton, Avatar, Text, cssVar } from '@cad-arc';
import { useUnitEmergencyUtils } from './use-unit-emergency-utils';
import { useUnitEmergency } from './use-unit-emergency';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';

function getEmergencyIcon(timer: boolean, isError: boolean, hasActiveEmergency: boolean) {
    if (hasActiveEmergency) {
        return 'SupervisorApproved';
    }
    if (isError) {
        return 'Rejected';
    }
    if (timer) {
        return 'Circle';
    }
    return 'Circle';
}

function getEmergencyClassName(timer: boolean, isError: boolean, hasActiveEmergency: boolean) {
    let cssClassName = '';
    if (timer) {
        cssClassName = 'timer';
    } else if (hasActiveEmergency) {
        cssClassName = 'success';
    } else if (isError) {
        cssClassName = 'error';
    }
    return cssClassName;
}

const buttonCSS = `
    background-color: var(--cad-colors-emergency-background);
    border: none;

    &[type="button"] {
        height: 60px;
        width: 60px;
    }

    &.timer {
        background-color: var(--cad-colors-emergency-background-activated);
        borderRadius: ${cssVar('arc.radii.md')};
    }

    &:hover:not(.success):not(.error) {
        background-color: var(--cad-colors-emergency-background-hover);
    }

    &.success:not(.error) {
        background-color: ${cssVar('arc.colors.raw.gray.200')};
    }

    &.error:not(.success) {
        background-color: var(--cad-colors-emergency-background-activated);
    }
`;

export function UnitEmergencyButton() {
    const { timer, secondsRemaining, handleEmergencyTimer } = useUnitEmergencyUtils();
    const { createUnitEmergencyMutation, activeUnitEmergency } = useUnitEmergency();
    const { shortcutKeyViews } = useBootstrapState();

    const unitEmergencyShortcutLabel = useMemo(() => {
        if (!shortcutKeyViews) {
            return '';
        }

        const targetKeyCombination = timer
            ? ShortcutCommandEnum.UNIT_EMERGENCY_CANCEL.name
            : ShortcutCommandEnum.UNIT_EMERGENCY.name;

        return (
            shortcutKeyViews.find(
                (view) =>
                    view.shortcutContext === ShortcutContextEnum.FIRST_RESPONDER.name &&
                    view.shortcutCommand === targetKeyCombination
            )?.shortcutKeyCombination ?? ''
        );
    }, [shortcutKeyViews, timer]);

    const emergencyIcon = getEmergencyIcon(
        timer,
        createUnitEmergencyMutation.isError,
        !!activeUnitEmergency
    );

    const emergencyCSSClassName = getEmergencyClassName(
        timer,
        createUnitEmergencyMutation.isError,
        !!activeUnitEmergency
    );

    return (
        <Box
            padding="2"
            display="flex"
            w="72px"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="1"
            data-testid="unit-emergency-button"
            marginTop="90px"
        >
            {!timer && (
                <>
                    <IconButton
                        aria-label="Send Emergency"
                        onClick={handleEmergencyTimer}
                        className={emergencyCSSClassName}
                        css={buttonCSS}
                        icon={
                            <Avatar
                                icon={emergencyIcon}
                                style={{
                                    background: 'inherit',
                                    color: "var(--cad-colors-emergency-inner-background)",
                                    height: '50px',
                                    width: '50px',
                                }}
                            />
                        }
                    />
                </>
            )}

            {timer && (
                <>
                    <IconButton
                        aria-label="Send Emergency"
                        style={{
                            borderRadius: 'var(--arc-radii-md, 6px)',
                            border: 'none',
                        }}
                        onClick={handleEmergencyTimer}
                        className={emergencyCSSClassName}
                        css={buttonCSS}
                        icon={
                            <>
                                <Avatar
                                    icon={emergencyIcon}
                                    style={{
                                        background: 'inherit',
                                        color: "var(--cad-colors-emergency-inner-background)",
                                        height: '50px',
                                        width: '50px',
                                    }}
                                />
                                <Text
                                    fontSize="lg"
                                    style={{
                                        color: "var(--cad-colors-emergency-countdownText)",
                                        position: 'absolute',
                                        marginTop: '0.1rem',
                                    }}
                                >
                                    {secondsRemaining}
                                </Text>
                            </>
                        }
                    />
                </>
            )}

            <Box
                textTransform="uppercase"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                textAlign="center"
                fontSize=".75rem"
                width="100%"
            >
                {unitEmergencyShortcutLabel}
            </Box>
        </Box>
    );
}

